import type { ReactNode } from 'react';
import { Children, isValidElement } from 'react';

import { useTheme } from '@emotion/react';
import { isNil } from 'ramda';

import { useBreakpoint } from '@twistopayments/design-system/hooks';
import { ChevronRightAltIcon } from '@twistopayments/design-system/icons/inline';

import { BannerContextProvider } from './banner.context';
import * as styles from './banner.styles';
import { Action } from './components';
import { BannerLink } from './subcomponents/banner-link';
import { BannerSubtitle } from './subcomponents/banner-subtitle';

export type BannerSize = 'medium' | 'small';
export type BannerVariant = 'primary' | 'secondary' | 'warning' | 'error';

type Props = {
  children: ReactNode;
  className?: string;
  size?: BannerSize;
  variant?: BannerVariant;
  href?: string;
  to?: string;
  onClick?: () => void;
  showArrow?: boolean;
};

export const Banner = ({
  children,
  className,
  size = 'medium',
  variant = 'primary',
  href,
  to,
  onClick,
  showArrow,
}: Props) => {
  const theme = useTheme();
  const hasAction = !isNil(href) || !isNil(to) || !isNil(onClick);
  const hasArrow = showArrow ?? (hasAction && size === 'medium');
  const hasSecondaryContent = Children.toArray(children).some(
    (child) => isValidElement(child) && (child.type === BannerSubtitle || child.type === BannerLink)
  );
  const isDesktop = useBreakpoint('smUp');

  return (
    <BannerContextProvider hasAction={hasAction} size={size} variant={variant}>
      <Action data-testid="banner" href={href} to={to} onClick={onClick}>
        <article
          className={className}
          css={[
            styles.container(theme),
            styles.containerSize[size],
            styles.containerBackground[variant],
            !hasSecondaryContent && styles.singleLine,
            hasAction && isDesktop && styles.containerHover,
            hasAction && isDesktop && styles.containerBackgroundHover[variant],
            hasAction && isDesktop && theme.presentation.banner.hover[variant],
          ]}>
          {children}
          {hasArrow ? (
            <ChevronRightAltIcon
              className="banner-arrow"
              color="inherit"
              css={[styles.arrow, styles.arrowColor[variant](theme), styles.arrowSize[size](theme)]}
            />
          ) : null}
        </article>
      </Action>
    </BannerContextProvider>
  );
};
